import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Image,
  Text,
  Heading,
  Button,
  Textarea,
  HStack,
  Flex,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Collapse,
  Spinner,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
  Badge,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { StarIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import {
  Product,
  ProductVariantAttribute,
  ProductVariantOption,
  SelectedProductVariant,
} from "../../types/product";
import {
  useAddReviewMutation,
  useDeleteProductMutation,
  useGetProductsQuery,
  useUpdateProductMutation,
} from "../../productApi/productAPI";
import FitmentBadge from "../../CarPartsModule/components/FitmentBadge";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import ProductCarouselSmall from "../Carousel/ProductsCarouselSmall";
import { useAuth } from "../../auth/AuthContext";
import { ProductFormModal } from "./ProductFormModal";
import { normalizeProductForCart } from "../../utils/normalizeCartProduct";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import { PickDenguShareButton } from "../Businesses/BusinessQrShareActions";

interface ProductDetailsProps {
  product: Product;
}

const cardsBorderRadii = "8px";
const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const normalize = (value: string = "") => value.toLowerCase().trim();

const PICKDENGU_PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_FRONTEND_URL ||
  process.env.REACT_APP_APP_URL ||
  "https://pickdengu.mw"
).replace(/\/+$/, "");

const tokenize = (value: string = "") =>
  normalize(value)
    .replace(/[^\w\s-]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 2);

const ExpandableTabContent: React.FC<{
  previewHeight?: number;
  children: React.ReactNode;
  theme: any;
}> = ({ children, previewHeight = 150, theme }) => {
  const [expanded, setExpanded] = useState(false);
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    const contentElement = contentRef.current;

    if (!contentElement) {
      setHasMoreContent(false);
      return;
    }

    const measureContent = () => {
      const contentHeight = contentElement.scrollHeight;
      const shouldShowToggle = contentHeight > previewHeight + 2;

      setHasMoreContent(shouldShowToggle);

      if (!shouldShowToggle) {
        setExpanded(false);
      }
    };

    const animationFrame = window.requestAnimationFrame(measureContent);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measureContent)
        : null;

    resizeObserver?.observe(contentElement);
    window.addEventListener("resize", measureContent);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measureContent);
    };
  }, [children, previewHeight]);

  return (
    <Box>
      <Collapse startingHeight={previewHeight} in={expanded || !hasMoreContent}>
        <Box ref={contentRef} color={theme.text}>
          {children}
        </Box>
      </Collapse>

      {hasMoreContent && (
        <Button
          mt={4}
          variant="ghost"
          size="sm"
          rightIcon={expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          onClick={() => setExpanded((prev) => !prev)}
          color={theme.primary}
          _hover={{ bg: theme.surfaceAlt }}
          borderRadius={theme.radii.sm}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const getDisplayImage = (item: any) =>
  item?.thumbnail ||
  (Array.isArray(item?.images) && item.images[0]) ||
  item?.imageUrl ||
  item?.image ||
  "https://picsum.photos/seed/fallback-related/400/400";

const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [addReview] = useAddReviewMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct, { isLoading: deletingProduct }] =
    useDeleteProductMutation();

  const { data: allProducts = [], isLoading: relatedLoading } =
    useGetProductsQuery();

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const editModal = useDisclosure();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const productOwnerId = String((product as any)?.userId || "");
  const loggedInUserId = String(user?.uid || "");
  const canManageProduct = Boolean(
    loggedInUserId && productOwnerId && loggedInUserId === productOwnerId,
  );

  const variantAttributes = useMemo<ProductVariantAttribute[]>(
    () =>
      Array.isArray(product?.variantAttributes)
        ? product.variantAttributes.filter(
            (attr) =>
              attr &&
              attr.name &&
              Array.isArray(attr.options) &&
              attr.options.length > 0,
          )
        : [],
    [product],
  );

  const buildInitialSelections = () => {
    const initial: Record<string, string> = {};

    variantAttributes.forEach((attr) => {
      const defaultValue =
        product?.defaultVariantSelections?.[attr.name] ||
        attr.options?.find((option) => option.isActive !== false)?.value ||
        attr.options?.[0]?.value ||
        "";

      if (defaultValue) {
        initial[attr.name] = defaultValue;
      }
    });

    return initial;
  };

  const [selectedVariantValues, setSelectedVariantValues] = useState<
    Record<string, string>
  >(buildInitialSelections);

  React.useEffect(() => {
    setSelectedVariantValues(buildInitialSelections());
  }, [product, variantAttributes.length]);

  const getOptionForAttribute = (
    attribute: ProductVariantAttribute,
  ): ProductVariantOption | undefined =>
    attribute.options.find(
      (option) =>
        option.value === selectedVariantValues[attribute.name] &&
        option.isActive !== false,
    ) || attribute.options.find((option) => option.isActive !== false);

  const selectedVariantOptions = useMemo(() => {
    return variantAttributes
      .map((attribute) => {
        const option = getOptionForAttribute(attribute);
        if (!option) return null;

        return {
          attribute,
          option,
        };
      })
      .filter(Boolean) as Array<{
      attribute: ProductVariantAttribute;
      option: ProductVariantOption;
    }>;
  }, [variantAttributes, selectedVariantValues]);

  const selectedVariants = useMemo<SelectedProductVariant[]>(() => {
    return selectedVariantOptions.map(({ attribute, option }) => ({
      attributeName: attribute.name,
      value: option.value,
      label: option.label,
      hex: option.hex,
    }));
  }, [selectedVariantOptions]);

  const selectedVariantPrice = useMemo(() => {
    const optionWithPrice = selectedVariantOptions.find(
      ({ option }) => option.price !== undefined && option.price !== null,
    );
    return optionWithPrice?.option?.price ?? product.price;
  }, [selectedVariantOptions, product.price]);

  const getOptionDisplayImages = (option?: ProductVariantOption) => {
    if (!option) return [];
    const optionImages = Array.isArray(option.images)
      ? option.images.filter(Boolean)
      : [];
    const fallbackSingle = option.imageUrl || option.thumbnail || "";

    return Array.from(
      new Set(
        [...optionImages, ...(fallbackSingle ? [fallbackSingle] : [])].filter(
          Boolean,
        ),
      ),
    );
  };

  const selectedVariantImages = useMemo(() => {
    const colorLike = selectedVariantOptions.find(
      ({ attribute, option }) =>
        attribute.type === "color" && getOptionDisplayImages(option).length > 0,
    );

    return colorLike ? getOptionDisplayImages(colorLike.option) : [];
  }, [selectedVariantOptions]);

  const images = useMemo(() => {
    const arr = Array.isArray(product?.images) ? product.images : [];
    const fallback = product?.imageUrl ? [product.imageUrl] : [];
    const mergedBase = [...arr, ...fallback].filter(Boolean);

    const merged =
      selectedVariantImages.length > 0
        ? [...selectedVariantImages, ...mergedBase]
        : mergedBase;

    return Array.from(new Set(merged));
  }, [product, selectedVariantImages]);

  const [selectedImage, setSelectedImage] = useState(images[0] || "");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewRating, setReviewRating] = useState(5);

  const lensRef = useRef<HTMLDivElement | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [bgPos, setBgPos] = useState("50% 50%");

  React.useEffect(() => {
    if (!selectedImage && images[0]) {
      setSelectedImage(images[0]);
      return;
    }

    if (selectedImage && images.length > 0 && !images.includes(selectedImage)) {
      setSelectedImage(images[0]);
    }
  }, [images, selectedImage]);

  React.useEffect(() => {
    if (selectedVariantImages.length > 0) {
      setSelectedImage(selectedVariantImages[0]);
    }
  }, [selectedVariantImages]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBgPos(`${clamp(x, 0, 100)}% ${clamp(y, 0, 100)}%`);
  };

  const handleSelectVariant = (
    attribute: ProductVariantAttribute,
    option: ProductVariantOption,
  ) => {
    setSelectedVariantValues((prev) => ({
      ...prev,
      [attribute.name]: option.value,
    }));

    const optionImages = getOptionDisplayImages(option);
    if (attribute.type === "color" && optionImages.length > 0) {
      setSelectedImage(optionImages[0]);
    }
  };

  const handleAddReview = async () => {
    if (product && reviewMessage.trim()) {
      await addReview({
        id: product._id!,
        review: {
          message: reviewMessage,
          rating: reviewRating,
        },
      });

      setReviewMessage("");
      setReviewRating(5);
    }
  };

  const handleAddToCart = () => {
    const productId = String((product as any)._id || (product as any).id || "");
    const finalImage = selectedImage || images[0] || product.imageUrl || "";

    dispatch(
      addToCart(
        normalizeProductForCart(product, {
          id: productId,
          _id: productId,
          productId,
          selectedVariants,
          selectedPrice: selectedVariantPrice,
          price: selectedVariantPrice,
          image: finalImage,
          imageUrl: finalImage,
          selectedImage: finalImage,
          quantity: 1,
        }),
      ),
    );

    toast({
      title: "Added to cart",
      description:
        selectedVariants.length > 0
          ? selectedVariants
              .map(
                (item) => `${item.attributeName}: ${item.label || item.value}`,
              )
              .join(" • ")
          : "Product added to cart",
      status: "success",
      duration: 2200,
      isClosable: true,
    });
  };

  const openEdit = (item: Product) => {
    setEditingProduct(item);
    editModal.onOpen();
  };

  const handleUpdate = async (payload: Partial<Product>) => {
    if (!editingProduct?._id) throw new Error("Missing product id");

    try {
      await updateProduct({
        id: editingProduct._id,
        updatedProduct: payload,
      }).unwrap();

      toast({
        title: "Product updated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      editModal.onClose();
      setEditingProduct(null);
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to update product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    if (!product?._id) return;

    try {
      await deleteProduct(product._id).unwrap();

      toast({
        title: "Product deleted",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      navigate("/products");
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to delete product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const relatedProducts = useMemo(() => {
    if (!allProducts?.length || !product) return [];

    const currentId = product._id;
    const currentCategory = normalize(product.category || "");
    const currentTags = Array.isArray((product as any).tags)
      ? (product as any).tags.map((tag: string) => normalize(tag))
      : [];
    const currentTitleWords = tokenize(product.title || "");

    return allProducts
      .filter((item: any) => item?._id !== currentId)
      .map((item: any) => {
        let score = 0;

        const itemCategory = normalize(item?.category || "");
        const itemTags = Array.isArray(item?.tags)
          ? item.tags.map((tag: string) => normalize(tag))
          : [];
        const itemTitleWords = tokenize(item?.title || "");

        if (
          itemCategory &&
          currentCategory &&
          itemCategory === currentCategory
        ) {
          score += 8;
        }

        const sharedTags = itemTags.filter((tag: string) =>
          currentTags.includes(tag),
        ).length;
        score += sharedTags * 3;

        const sharedTitleWords = itemTitleWords.filter((word: string) =>
          currentTitleWords.includes(word),
        ).length;
        score += sharedTitleWords * 2;

        if (item?.isFeatured) score += 1;
        if (typeof item?.ratingAverage === "number")
          score += item.ratingAverage / 5;

        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map(({ item }) => ({
        ...item,

        id: String(item?._id || item?.id || item?.slug || item?.title),
        _id: String(item?._id || item?.id || ""),
        productId: String(item?.productId || item?._id || item?.id || ""),

        userId: item?.userId || "",
        sellerUserId: item?.sellerUserId || item?.ownerId || item?.userId || "",
        ownerId: item?.ownerId || item?.sellerUserId || item?.userId || "",
        sellerBusinessName:
          item?.sellerBusinessName ||
          item?.businessName ||
          item?.sellerName ||
          item?.shopName ||
          "",
        dispatchWarehouseId: item?.dispatchWarehouseId || "",

        title: item?.title || "Untitled Product",
        image: getDisplayImage(item),
        imageUrl: getDisplayImage(item),
        images: Array.isArray(item?.images) ? item.images : [],
        description:
          item?.description ||
          `${item?.category || "General"} product you may also like.`,
        category: item?.category || "Car Parts",
        price: Number(item?.price || 0),

        weightKg: Number(item?.weightKg || 0),
        lengthCm: Number(item?.lengthCm || 0),
        widthCm: Number(item?.widthCm || 0),
        heightCm: Number(item?.heightCm || 0),
        bulky: Boolean(item?.bulky),
      }));
  }, [allProducts, product]);

  const averageRating = useMemo(() => {
    if (!product?.reviews?.length) return 0;
    const total = product.reviews.reduce(
      (sum, review) => sum + (review.rating || 0),
      0,
    );
    return total / product.reviews.length;
  }, [product]);

  const reviewBreakdown = useMemo(() => {
    const counts = [5, 4, 3, 2, 1].map((star) => ({
      star,
      count:
        product?.reviews?.filter((review) => review.rating === star).length ||
        0,
    }));

    const total = product?.reviews?.length || 0;

    return counts.map((item) => ({
      ...item,
      percent: total ? (item.count / total) * 100 : 0,
    }));
  }, [product]);

  const deliveryInfo = [
    "Standard delivery usually takes 2–5 business days depending on your location.",
    "Orders are processed during working hours and tracking is shared once dispatched.",
    "Some bulky, fragile, or marketplace items may arrive separately.",
    "Shipping fees are calculated at checkout based on address and item size.",
    "Rural or high-demand zones may require extra delivery time.",
  ];

  const returnPolicy = [
    "Returns are typically accepted within 7–14 days if the product is unused and in original condition.",
    "The item should be returned with all accessories, packaging, and proof of purchase.",
    "Customized, intimate, digital, or hygiene-sensitive items may not qualify for return.",
    "If the wrong or damaged item is delivered, report it as soon as possible.",
    "Refunds are processed after item inspection and depend on the original payment method.",
  ];

  const productShareId = String(
    (product as any)?._id ||
      (product as any)?.id ||
      (product as any)?.productId ||
      "",
  ).trim();

  const productShareUrl = productShareId
    ? `${PICKDENGU_PUBLIC_WEB_URL}/product/${encodeURIComponent(
        productShareId,
      )}`
    : typeof window !== "undefined"
      ? `${PICKDENGU_PUBLIC_WEB_URL}${window.location.pathname}`
      : PICKDENGU_PUBLIC_WEB_URL;

  const productShareText = [
    product.title,
    product.category,
    `MWK ${Number(selectedVariantPrice || product.price || 0).toLocaleString()}`,
    product.description?.trim().slice(0, 160),
  ]
    .filter(Boolean)
    .join(" • ");

  const cardBg = theme.gradients.shellBg;
  const cardBorder = theme.border;
  const shadow = theme.shadowMd;
  const textMain = theme.text;
  const textMuted = theme.textMuted;
  const divider = theme.divider;

  const primaryBtn = theme.buttons.tealPrimary || theme.buttons.primary;
  const secondaryBtn = theme.buttons.secondary;

  return (
    <Box>
      <Flex
        maxW="100%"
        mx="auto"
        justifyContent="space-between"
        gap={{ base: "18px", md: "30px" }}
        direction={{ base: "column", lg: "row" }}
      >
        <Box
          p={{ base: 4, md: 5 }}
          minW={{ base: "100%", lg: "680px" }}
          borderRadius={theme.radii.xl}
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow={shadow}
          bgGradient={cardBg}
          backdropFilter="blur(14px)"
          overflow="hidden"
          position="relative"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="1px"
            bgGradient={`linear(to-r, ${theme.accents.blue}, ${theme.accents.violet}, ${theme.teal[500]})`}
            opacity={0.65}
          />

          <Box position="relative">
            <Heading size="lg" mb={2} color={textMain}>
              {product.title}
            </Heading>

            <Text color={textMuted} fontSize="sm" mb={3}>
              Category:{" "}
              <Box as="span" color={textMain} fontWeight="600">
                {product.category || "General"}
              </Box>
            </Text>

            <FitmentBadge partId={product._id!} />

            <Flex mt={3} mb={4} align="center" justify="space-between" gap={3}>
              <Text fontSize="2xl" color={theme.primary} fontWeight="800">
                MWK {Number(selectedVariantPrice || 0).toLocaleString()}
              </Text>

              {canManageProduct && (
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Edit product"
                    icon={<Pencil size={16} />}
                    onClick={() => openEdit(product)}
                    {...secondaryBtn}
                    borderRadius={theme.radii.sm}
                  />
                  <IconButton
                    aria-label="Delete product"
                    icon={<Trash2 size={16} />}
                    onClick={handleDelete}
                    isLoading={deletingProduct}
                    bg={theme.danger}
                    color="white"
                    _hover={{ filter: "brightness(0.95)" }}
                    borderRadius={theme.radii.sm}
                  />
                </HStack>
              )}
            </Flex>

            {variantAttributes.length > 0 && (
              <Flex
                mb={5}
                gap={4}
                wrap={{ base: "wrap", xl: "nowrap" }}
                align="stretch"
              >
                {[...variantAttributes]
                  .sort((a, b) => {
                    const getRank = (name: string) => {
                      const n = name.toLowerCase();
                      if (n === "color") return 0;
                      if (n === "size") return 1;
                      return 2;
                    };
                    return getRank(a.name) - getRank(b.name);
                  })
                  .map((attribute) => {
                    const selectedValue = selectedVariantValues[attribute.name];
                    const activeOptions = attribute.options.filter(
                      (option) => option.isActive !== false,
                    );

                    const isColor = attribute.name.toLowerCase() === "color";
                    const isSize = attribute.name.toLowerCase() === "size";

                    return (
                      <Box
                        key={attribute.name}
                        flex={isColor ? "2.4" : isSize ? "1" : "1.2"}
                        minW={
                          isColor
                            ? { base: "100%", lg: "320px" }
                            : isSize
                              ? { base: "100%", lg: "260px" }
                              : { base: "100%", lg: "280px" }
                        }
                        border={`1px solid ${theme.border}`}
                        borderRadius={cardsBorderRadii}
                        bg={theme.surfaceAlt}
                        px={4}
                        py={3}
                      >
                        <Text
                          fontWeight="700"
                          color={theme.textMuted}
                          fontSize="sm"
                          mb={3}
                        >
                          {attribute.name}
                        </Text>

                        <Wrap spacing={2}>
                          {activeOptions.map((option) => {
                            const isSelected = selectedValue === option.value;
                            const optionIsColor = attribute.type === "color";

                            return (
                              <WrapItem
                                key={`${attribute.name}-${option.value}`}
                              >
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleSelectVariant(attribute, option)
                                  }
                                  variant="outline"
                                  borderRadius={theme.radii.sm}
                                  borderColor={
                                    isSelected ? theme.primary : theme.border
                                  }
                                  bg={
                                    isSelected ? theme.surface : "transparent"
                                  }
                                  color={theme.text}
                                  maxW="100%"
                                  whiteSpace="nowrap"
                                  leftIcon={
                                    optionIsColor && option.hex ? (
                                      <Box
                                        w="14px"
                                        h="14px"
                                        borderRadius={cardsBorderRadii}
                                        bg={option.hex}
                                        border="1px solid rgba(255,255,255,0.35)"
                                      />
                                    ) : undefined
                                  }
                                  _hover={{ bg: theme.surface }}
                                  boxShadow={
                                    isSelected ? theme.shadowSm : "none"
                                  }
                                >
                                  {option.label || option.value}
                                </Button>
                              </WrapItem>
                            );
                          })}
                        </Wrap>
                      </Box>
                    );
                  })}
              </Flex>
            )}

            <Box
              ref={lensRef}
              borderRadius={theme.radii.lg}
              border={`1px solid ${divider}`}
              bg={isDark ? "rgba(255,255,255,0.03)" : "#fff"}
              overflow="hidden"
              position="relative"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setBgPos("50% 50%");
              }}
              onMouseMove={handleMouseMove}
              cursor="zoom-in"
              height={{ base: "320px", md: "420px" }}
              mb={4}
            >
              <Image
                src={selectedImage}
                alt={product.title}
                w="100%"
                h="100%"
                objectFit="cover"
                objectPosition="center"
                bg="#fff"
                opacity={isHovering ? 0 : 1}
                transition="opacity 180ms ease"
              />

              <Box
                position="absolute"
                inset={0}
                backgroundImage={`url(${selectedImage})`}
                backgroundRepeat="no-repeat"
                backgroundSize="cover"
                backgroundPosition={bgPos}
                opacity={isHovering ? 1 : 0}
                transform={isHovering ? "scale(1.12)" : "scale(1)"}
                transformOrigin={bgPos}
                transition="opacity 180ms ease, transform 220ms ease"
              />

              <Box
                position="absolute"
                bottom="12px"
                left="12px"
                px="10px"
                py="6px"
                borderRadius={theme.radii.pill}
                fontSize="xs"
                color={
                  isDark ? "rgba(255,255,255,0.90)" : "rgba(15,23,42,0.75)"
                }
                bg={isDark ? "rgba(2,6,23,0.55)" : "#fff"}
                border={`1px solid ${divider}`}
                backdropFilter="blur(10px)"
                pointerEvents="none"
              >
                Hover to zoom • Move cursor to pan
              </Box>
            </Box>

            <HStack
              spacing={2}
              overflowX="auto"
              pb={1}
              sx={{
                "&::-webkit-scrollbar": { height: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  background: isDark
                    ? "rgba(255,255,255,0.14)"
                    : "rgba(15,23,42,0.12)",
                  borderRadius: {cardsBorderRadii}
                },
              }}
            >
              {images.map((img, idx) => {
                const active = selectedImage === img;

                return (
                  <Box
                    key={`${img}-${idx}`}
                    borderRadius={theme.radii.sm}
                    border={`1px solid ${active ? theme.primary : divider}`}
                    overflow="hidden"
                    flexShrink={0}
                    mt={2}
                    cursor="pointer"
                    onClick={() => setSelectedImage(img)}
                    transform={active ? "translateY(-1px)" : "none"}
                    transition="all 0.18s ease"
                    boxShadow={
                      active
                        ? isDark
                          ? "0 10px 24px rgba(0,0,0,0.35)"
                          : "0 10px 24px rgba(0,0,0,0.10)"
                        : "none"
                    }
                  >
                    <Image
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      boxSize="64px"
                      objectFit="cover"
                      transition="transform 0.25s ease"
                      _hover={{ transform: "scale(1.06)" }}
                    />
                  </Box>
                );
              })}
            </HStack>

            <Box
              position="relative"
              mt="30px"
              display={{ base: "block", md: "none" }}
            >
              <HStack spacing={3} align="stretch" w="full">
                <Button
                  {...primaryBtn}
                  onClick={handleAddToCart}
                  flex="1"
                  borderRadius={theme.radii.sm}
                  whiteSpace="nowrap"
                >
                  Add to Cart
                </Button>

                <PickDenguShareButton
                  shareTitle={`${product.title} on PickDengu`}
                  shareText={productShareText}
                  shareUrl={productShareUrl}
                  modalTitle={`Share ${product.title}`}
                  modalDescription="Share this product details page."
                  buttonProps={{
                    ...secondaryBtn,
                    flexShrink: 0,
                    minW: "110px",
                    borderRadius: theme.radii.sm,
                  }}
                />
              </HStack>
            </Box>
          </Box>
        </Box>

        <Box
          pt={{ base: 1, md: 1 }}
          width="100%"
          borderRadius={theme.radii.md}
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow={shadow}
          bgGradient={cardBg}
          backdropFilter="blur(14px)"
          overflow="hidden"
          position="relative"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="1px"
            bgGradient={`linear(to-r, ${theme.teal[500]}, ${theme.accents.violet})`}
            opacity={0.45}
          />

          <Box p={{ base: 4, md: 5 }}>
            <Tabs variant="enclosed" colorScheme="teal">
              <TabList
                overflowX="auto"
                borderColor={divider}
                sx={{
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                <Tab
                  color={textMuted}
                  _selected={{ color: textMain, bg: theme.surface }}
                >
                  Details
                </Tab>
                <Tab
                  color={textMuted}
                  _selected={{ color: textMain, bg: theme.surface }}
                >
                  Delivery
                </Tab>
                <Tab
                  color={textMuted}
                  _selected={{ color: textMain, bg: theme.surface }}
                >
                  Returns
                </Tab>
                <Tab
                  color={textMuted}
                  _selected={{ color: textMain, bg: theme.surface }}
                >
                  Reviews
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <ExpandableTabContent theme={theme}>
                    <Text color={textMuted} lineHeight="1.8">
                      {product.description ||
                        "No product description available."}
                    </Text>
                  </ExpandableTabContent>
                </TabPanel>

                <TabPanel>
                  <ExpandableTabContent theme={theme}>
                    <VStack align="stretch" spacing={3}>
                      {deliveryInfo.map((item) => (
                        <Text key={item} color={textMuted}>
                          • {item}
                        </Text>
                      ))}
                    </VStack>
                  </ExpandableTabContent>
                </TabPanel>

                <TabPanel>
                  <ExpandableTabContent theme={theme}>
                    <VStack align="stretch" spacing={3}>
                      {returnPolicy.map((item) => (
                        <Text key={item} color={textMuted}>
                          • {item}
                        </Text>
                      ))}
                    </VStack>
                  </ExpandableTabContent>
                </TabPanel>

                <TabPanel>
                  <ExpandableTabContent theme={theme}>
                    <Box>
                      <Flex align="center" gap={2} mb={4}>
                        <Text fontSize="xl" fontWeight="800" color={textMain}>
                          {averageRating.toFixed(1)}
                        </Text>
                        <HStack spacing={1}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                              key={star}
                              color={
                                star <= Math.round(averageRating)
                                  ? "yellow.400"
                                  : "gray.400"
                              }
                            />
                          ))}
                        </HStack>
                        <Text color={textMuted}>
                          ({product?.reviews?.length || 0} reviews)
                        </Text>
                      </Flex>

                      <VStack align="stretch" spacing={2} mb={5}>
                        {reviewBreakdown.map((item) => (
                          <Flex key={item.star} align="center" gap={3}>
                            <Text color={textMuted} minW="40px">
                              {item.star}★
                            </Text>
                            <Box
                              flex="1"
                              h="8px"
                              bg={theme.surfaceAlt}
                              borderRadius={cardsBorderRadii}
                            >
                              <Box
                                h="8px"
                                w={`${item.percent}%`}
                                bg={theme.primary}
                                borderRadius={cardsBorderRadii}
                              />
                            </Box>
                            <Text color={textMuted} minW="30px">
                              {item.count}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>

                      <Divider my={4} borderColor={divider} />

                      <Textarea
                        value={reviewMessage}
                        onChange={(e) => setReviewMessage(e.target.value)}
                        placeholder="Write your review..."
                        bg={theme.surfaceAlt}
                        color={theme.text}
                        borderColor={theme.border}
                        mb={3}
                      />

                      <HStack mb={4}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <IconButton
                            key={rating}
                            aria-label={`${rating} star rating`}
                            icon={<StarIcon />}
                            color={
                              rating <= reviewRating ? "yellow.400" : "gray.400"
                            }
                            variant="ghost"
                            onClick={() => setReviewRating(rating)}
                          />
                        ))}

                        <Button
                          {...primaryBtn}
                          size="sm"
                          onClick={handleAddReview}
                          isDisabled={!reviewMessage.trim()}
                        >
                          Submit Review
                        </Button>
                      </HStack>

                      {product?.reviews?.length ? (
                        <Box>
                          {product.reviews.map((review: any, index: number) => (
                            <Box
                              key={`${review.message}-${index}`}
                              p={3}
                              borderRadius={theme.radii.sm}
                              border={`1px solid ${theme.border}`}
                              bg={theme.surfaceAlt}
                              mb={3}
                            >
                              <HStack mb={1}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon
                                    key={star}
                                    boxSize={3}
                                    color={
                                      star <= review.rating
                                        ? "yellow.400"
                                        : "gray.400"
                                    }
                                  />
                                ))}
                              </HStack>
                              <Text color={textMuted}>{review.message}</Text>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Text color={textMuted}>
                          No reviews yet. Be the first to review this product.
                        </Text>
                      )}
                    </Box>
                  </ExpandableTabContent>
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Box
              position="relative"
              p={2}
              display={{ base: "none", md: "block" }}
            >
              <HStack spacing={3} align="stretch">
                <Button
                  {...primaryBtn}
                  onClick={handleAddToCart}
                  w="200px"
                  borderRadius={theme.radii.sm}
                  whiteSpace="nowrap"
                >
                  Add to Cart
                </Button>

                <PickDenguShareButton
                  shareTitle={`${product.title} on PickDengu`}
                  shareText={productShareText}
                  shareUrl={productShareUrl}
                  modalTitle={`Share ${product.title}`}
                  modalDescription="Share this product details page."
                  buttonProps={{
                    ...secondaryBtn,
                    minW: "120px",
                    borderRadius: theme.radii.sm,
                  }}
                />
              </HStack>
            </Box>
          </Box>
        </Box>
      </Flex>

      <Box
        mt={{ base: 5, md: 7 }}
        p={{ base: 4, md: 5 }}
        borderRadius={theme.radii.xl}
        borderWidth="1px"
        borderColor={cardBorder}
        boxShadow={shadow}
        bgGradient={cardBg}
        backdropFilter="blur(14px)"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="1px"
          bgGradient={`linear(to-r, ${theme.accents.blue}, ${theme.accents.violet}, ${theme.teal[500]})`}
          opacity={0.55}
        />

        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          mb={4}
        >
          <Box>
            <Heading size="md" color={theme.text}>
              You might also like
            </Heading>
            <Text color={theme.textMuted} mt={1}>
              Similar products selected from the same store.
            </Text>
          </Box>
        </Flex>

        {relatedLoading ? (
          <Flex minH="160px" align="center" justify="center">
            <GlobalSpinner />
          </Flex>
        ) : relatedProducts.length > 0 ? (
          <ProductCarouselSmall products={relatedProducts} />
        ) : (
          <Box
            p={6}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border={`1px solid ${theme.border}`}
          >
            <Text color={theme.textMuted}>
              No related products found yet for this item.
            </Text>
          </Box>
        )}
      </Box>

      <ProductFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.onClose();
          setEditingProduct(null);
        }}
        mode="edit"
        product={editingProduct}
        userId={user?.uid}
        onSubmit={handleUpdate}
      />
    </Box>
  );
};

export default ProductDetails;
